/**
 * Organization feature reducer.
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
    fetchMyOrganization,
    createOrganization,
    renameOrganization,
    fetchOrganizationMembers,
    updateOrganizationMemberRole,
    removeOrganizationMember,
    fetchOrganizationInvitations,
    createOrganizationInvitation,
    revokeOrganizationInvitation,
} from '../actions/organizationThunks';
import { ApiErrorPayload } from '../facades/apiFacade';
import { AdminInvitation, Organization, OrgMember } from '../../@types/organization';

type RequestStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

export interface OrganizationMembersState {
    data: OrgMember[];
    status: RequestStatus;
    error: string | null;
    updatingMemberIds: string[];
    removingMemberIds: string[];
}

export interface OrganizationInvitationsState {
    data: AdminInvitation[];
    status: RequestStatus;
    error: string | null;
    creating: boolean;
    revokingInvitationIds: string[];
}

export interface OrganizationState {
    data: Organization | null;
    isLoading: boolean;
    error: string | null;
    // True once we've confirmed (via a 404 from GET /organizations) that the
    // current user is floating — distinct from a real fetch failure.
    notMember: boolean;
    members: OrganizationMembersState;
    invitations: OrganizationInvitationsState;
}

const initialState: OrganizationState = {
    data: null,
    isLoading: false,
    error: null,
    notMember: false,
    members: {
        data: [],
        status: 'idle',
        error: null,
        updatingMemberIds: [],
        removingMemberIds: [],
    },
    invitations: {
        data: [],
        status: 'idle',
        error: null,
        creating: false,
        revokingInvitationIds: [],
    },
};

function removeId(ids: string[], id: string): string[] {
    return ids.filter((value) => value !== id);
}

function setMemberError(state: OrganizationState, action: { payload?: unknown; error: { message?: string } }): void {
    const payload = action.payload as ApiErrorPayload | undefined;
    state.members.error = payload?.message ?? action.error.message ?? 'Failed to manage organization members';
}

function setInvitationError(state: OrganizationState, action: { payload?: unknown; error: { message?: string } }): void {
    const payload = action.payload as ApiErrorPayload | undefined;
    state.invitations.error = payload?.message ?? action.error.message ?? 'Failed to manage invitations';
}

export const organizationSlice = createSlice({
    name: 'organization',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchMyOrganization.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchMyOrganization.fulfilled, (state, action: PayloadAction<Organization>) => {
                state.isLoading = false;
                state.data = action.payload;
                state.notMember = false;
            })
            .addCase(fetchMyOrganization.rejected, (state, action) => {
                state.isLoading = false;
                const payload = action.payload as ApiErrorPayload | undefined;
                if (payload?.status === 404) {
                    state.data = null;
                    state.notMember = true;
                } else {
                    state.error = payload?.message ?? action.error.message ?? 'Failed to fetch organization';
                }
            })

            .addCase(createOrganization.fulfilled, (state, action: PayloadAction<Organization>) => {
                state.data = action.payload;
                state.notMember = false;
                state.error = null;
            })
            .addCase(createOrganization.rejected, (state, action) => {
                const payload = action.payload as ApiErrorPayload | undefined;
                state.error = payload?.message ?? action.error.message ?? 'Failed to create organization';
            })

            .addCase(renameOrganization.fulfilled, (state, action: PayloadAction<Organization>) => {
                state.data = action.payload;
                state.error = null;
            })
            .addCase(renameOrganization.rejected, (state, action) => {
                const payload = action.payload as ApiErrorPayload | undefined;
                state.error = payload?.message ?? action.error.message ?? 'Failed to rename organization';
            })

            .addCase(fetchOrganizationMembers.pending, (state) => {
                state.members.status = 'loading';
                state.members.error = null;
            })
            .addCase(fetchOrganizationMembers.fulfilled, (state, action: PayloadAction<OrgMember[]>) => {
                state.members.status = 'succeeded';
                state.members.data = action.payload;
                state.members.error = null;
            })
            .addCase(fetchOrganizationMembers.rejected, (state, action) => {
                state.members.status = 'failed';
                setMemberError(state, action);
            })

            .addCase(updateOrganizationMemberRole.pending, (state, action) => {
                state.members.error = null;
                state.members.updatingMemberIds.push(action.meta.arg.userObjectId);
            })
            .addCase(updateOrganizationMemberRole.fulfilled, (state, action: PayloadAction<OrgMember>) => {
                state.members.updatingMemberIds = removeId(state.members.updatingMemberIds, action.payload.user_object_id);
                const index = state.members.data.findIndex((member) => member.user_object_id === action.payload.user_object_id);
                if (index >= 0) state.members.data[index] = action.payload;
            })
            .addCase(updateOrganizationMemberRole.rejected, (state, action) => {
                state.members.updatingMemberIds = removeId(state.members.updatingMemberIds, action.meta.arg.userObjectId);
                setMemberError(state, action);
            })

            .addCase(removeOrganizationMember.pending, (state, action) => {
                state.members.error = null;
                state.members.removingMemberIds.push(action.meta.arg.userObjectId);
            })
            .addCase(removeOrganizationMember.fulfilled, (state, action: PayloadAction<string>) => {
                state.members.removingMemberIds = removeId(state.members.removingMemberIds, action.payload);
                state.members.data = state.members.data.filter((member) => member.user_object_id !== action.payload);
            })
            .addCase(removeOrganizationMember.rejected, (state, action) => {
                state.members.removingMemberIds = removeId(state.members.removingMemberIds, action.meta.arg.userObjectId);
                setMemberError(state, action);
            })

            .addCase(fetchOrganizationInvitations.pending, (state) => {
                state.invitations.status = 'loading';
                state.invitations.error = null;
            })
            .addCase(fetchOrganizationInvitations.fulfilled, (state, action: PayloadAction<AdminInvitation[]>) => {
                state.invitations.status = 'succeeded';
                state.invitations.data = action.payload;
                state.invitations.error = null;
            })
            .addCase(fetchOrganizationInvitations.rejected, (state, action) => {
                state.invitations.status = 'failed';
                setInvitationError(state, action);
            })

            .addCase(createOrganizationInvitation.pending, (state) => {
                state.invitations.creating = true;
                state.invitations.error = null;
            })
            .addCase(createOrganizationInvitation.fulfilled, (state, action: PayloadAction<AdminInvitation>) => {
                state.invitations.creating = false;
                state.invitations.error = null;
                state.invitations.data.push(action.payload);
            })
            .addCase(createOrganizationInvitation.rejected, (state, action) => {
                state.invitations.creating = false;
                setInvitationError(state, action);
            })

            .addCase(revokeOrganizationInvitation.pending, (state, action) => {
                state.invitations.error = null;
                state.invitations.revokingInvitationIds.push(action.meta.arg.invitationObjectId);
            })
            .addCase(revokeOrganizationInvitation.fulfilled, (state, action: PayloadAction<string>) => {
                state.invitations.revokingInvitationIds = removeId(state.invitations.revokingInvitationIds, action.payload);
                state.invitations.data = state.invitations.data.filter((invitation) => invitation.object_id !== action.payload);
            })
            .addCase(revokeOrganizationInvitation.rejected, (state, action) => {
                state.invitations.revokingInvitationIds = removeId(
                    state.invitations.revokingInvitationIds,
                    action.meta.arg.invitationObjectId
                );
                setInvitationError(state, action);
            });
    },
});

export const organizationReducer = organizationSlice.reducer;
